import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, CalendarRange, DoorOpen, FileDown, Pencil, Plus, ShieldAlert, Trash2, Upload, Warehouse } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Select,
  Table,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import { ImportBuildingsModal } from '../components/ImportBuildingsModal';
import { RemoveRecordsDialog, useIsEdvoraStaff } from '../components/remove-records';
import { api, apiUrl, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatUtcDate, ROOM_KIND_LABEL } from '../lib/format';
import { invalidateInventory, useBuildings } from '../lib/hooks';
import { BUILDING_KIND_LABEL, isOpenOn, localToday } from '../lib/locations';
import type { Building, BuildingKind, Room, RoomKind } from '../lib/types';

/** A badge when a building or room isn't in use today, or is about to close. */
export function PlaceDates({ place }: { place: { openedOn?: string | null; closedOn?: string | null } }) {
  const today = localToday();
  if (place.closedOn && place.closedOn <= today) return <Badge tone="red">Closed {formatUtcDate(place.closedOn)}</Badge>;
  if (place.openedOn && place.openedOn > today) return <Badge tone="amber">Opens {formatUtcDate(place.openedOn)}</Badge>;
  if (place.closedOn) return <Badge tone="amber">Closes {formatUtcDate(place.closedOn)}</Badge>;
  return null;
}

function DateFields({ openedOn, closedOn, onChange }: { openedOn: string; closedOn: string; onChange(dates: { openedOn: string; closedOn: string }): void }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <Field label="In use from" hint="Optional">
        <Input type="date" value={openedOn} onChange={(e) => onChange({ openedOn: e.target.value, closedOn })} />
      </Field>
      <Field label="Closed from" hint="Its history stays">
        <Input type="date" value={closedOn} onChange={(e) => onChange({ openedOn, closedOn: e.target.value })} />
      </Field>
    </div>
  );
}

function BuildingModal({ open, onClose, building }: { open: boolean; onClose(): void; building?: Building | null }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', code: '', kind: 'CAMPUS' as BuildingKind, address: '', notes: '', openedOn: '', closedOn: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      name: building?.name ?? '',
      code: building?.code ?? '',
      kind: building?.kind ?? 'CAMPUS',
      address: building?.address ?? '',
      notes: building?.notes ?? '',
      openedOn: building?.openedOn ?? '',
      closedOn: building?.closedOn ?? '',
    });
  }, [open, building]);

  const mutation = useMutation({
    mutationFn: () => (building ? api.patch(`/buildings/${building.id}`, form) : api.post('/buildings', form)),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(building ? 'Saved' : form.kind === 'WAREHOUSE' ? 'Warehouse added' : 'Building added');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={building ? `Edit ${building.name}` : 'Add a building or warehouse'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="building-form" loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="building-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Type">
          <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as BuildingKind })}>
            {(Object.keys(BUILDING_KIND_LABEL) as BuildingKind[]).map((kind) => (
              <option key={kind} value={kind}>
                {BUILDING_KIND_LABEL[kind]}
              </option>
            ))}
          </Select>
        </Field>
        {form.kind === 'WAREHOUSE' && (
          <p className="-mt-2 text-xs text-slate-500">For devices that live off campus: depot stock, end-of-life inventory, district loaners.</p>
        )}
        <Field label="Name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} autoFocus />
        </Field>
        <Field label="Short code" hint="Optional, e.g. US or MS">
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} maxLength={20} />
        </Field>
        <Field label="Address">
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} maxLength={300} />
        </Field>
        <DateFields openedOn={form.openedOn} closedOn={form.closedOn} onChange={(dates) => setForm({ ...form, ...dates })} />
        <Field label="Notes">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={2000} />
        </Field>
      </form>
    </Modal>
  );
}

export function RoomModal({
  open,
  onClose,
  room,
  buildingId,
}: {
  open: boolean;
  onClose(): void;
  room?: Room | null;
  buildingId?: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const buildings = useBuildings();
  const [form, setForm] = useState({ buildingId: '', name: '', roomNumber: '', kind: 'CLASSROOM', capacity: '', notes: '', openedOn: '', closedOn: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      buildingId: room?.buildingId ?? buildingId ?? '',
      name: room?.name ?? '',
      roomNumber: room?.roomNumber ?? '',
      kind: room?.kind ?? 'CLASSROOM',
      capacity: room?.capacity === null || room?.capacity === undefined ? '' : String(room.capacity),
      notes: room?.notes ?? '',
      openedOn: room?.openedOn ?? '',
      closedOn: room?.closedOn ?? '',
    });
  }, [open, room, buildingId]);

  const mutation = useMutation({
    mutationFn: () => {
      // An empty capacity clears it rather than being sent as "".
      const body = { ...form, capacity: form.capacity.trim() === '' ? null : Number(form.capacity) };
      return room ? api.patch(`/rooms/${room.id}`, body) : api.post('/rooms', body);
    },
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(room ? 'Room saved' : 'Room added');
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={room ? 'Edit room' : 'Add room'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="room-form" loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="room-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Building" required>
          <Select value={form.buildingId} onChange={(e) => setForm({ ...form, buildingId: e.target.value })} required>
            <option value="">Select a building</option>
            {buildings.data
              ?.filter((b) => isOpenOn(b) || b.id === form.buildingId)
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Room name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} placeholder="Room 204, Library, STEM Lab…" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Room number">
            <Input value={form.roomNumber} onChange={(e) => setForm({ ...form, roomNumber: e.target.value })} maxLength={40} />
          </Field>
          <Field label="Seats" hint="Optional">
            <Input type="number" min={0} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="w-28" />
          </Field>
          <Field label="Type">
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {(Object.keys(ROOM_KIND_LABEL) as RoomKind[]).map((kind) => (
                <option key={kind} value={kind}>
                  {ROOM_KIND_LABEL[kind]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <DateFields openedOn={form.openedOn} closedOn={form.closedOn} onChange={(dates) => setForm({ ...form, ...dates })} />
        {room && form.closedOn && form.closedOn !== room.closedOn && (
          <p className="-mt-2 text-xs text-slate-500">People responsible for it stop being responsible that day. Move its devices and carts out first.</p>
        )}
        <Field label="Notes">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={2000} />
        </Field>
      </form>
    </Modal>
  );
}

function ResponsibilityReportModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const [date, setDate] = useState(localToday());
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Who was responsible"
      description="Every device as it was at the end of a day: where it was kept, who had it checked out, and who was responsible for those rooms."
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <a href={apiUrl('/locations/responsibility.csv', { date })} aria-disabled={!date}>
            <Button variant="primary" icon={<FileDown />} disabled={!date}>
              Download CSV
            </Button>
          </a>
        </>
      }
    >
      <Field label="Date">
        <Input type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} className="w-44" />
      </Field>
    </Modal>
  );
}

function BuildingCard({
  building,
  canWrite,
  isAdmin,
  onAddRoom,
  onEdit,
  onEditRoom,
  onDelete,
  onRemove,
  edvora,
}: {
  building: Building;
  canWrite: boolean;
  isAdmin: boolean;
  onAddRoom(): void;
  onEdit(): void;
  onEditRoom(room: Room): void;
  onDelete(target: { kind: 'building' | 'room'; id: string; name: string }): void;
  /** Edvora staff only: removes a place that has history but nothing in it. */
  onRemove(target: { kind: 'building' | 'room'; id: string; name: string }): void;
  edvora: boolean;
}) {
  const warehouse = building.kind === 'WAREHOUSE';
  const Icon = warehouse ? Warehouse : Building2;
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className={`rounded-lg p-2 ${warehouse ? 'bg-violet-50 text-violet-700' : 'bg-brand-50 text-brand-700'}`}>
            <Icon className="size-5" />
          </div>
          <div>
            <h2 className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">
              {building.name}
              {building.code && <Badge>{building.code}</Badge>}
              {warehouse && <Badge tone="violet">Off campus</Badge>}
              <PlaceDates place={building} />
            </h2>
            <p className="text-sm text-slate-500">
              {building._count.rooms} rooms · {building._count.assets} devices kept here
              {building._count.carts > 0 && ` · ${building._count.carts} carts`} · {building._count.checkouts} assigned here
              {building.address && ` · ${building.address}`}
            </p>
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" icon={<Plus />} onClick={onAddRoom} disabled={!isOpenOn(building)}>
              Room
            </Button>
            <Button size="sm" variant="ghost" icon={<Pencil />} onClick={onEdit}>
              Edit
            </Button>
            {isAdmin && (
              <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => onDelete({ kind: 'building', id: building.id, name: building.name })}>
                Delete
              </Button>
            )}
            {edvora && (
              <Button size="sm" variant="ghost" icon={<ShieldAlert />} onClick={() => onRemove({ kind: 'building', id: building.id, name: building.name })}>
                Remove (Edvora)
              </Button>
            )}
          </div>
        )}
      </div>
      {building.rooms.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">No rooms yet.</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Room</Th>
              <Th>Number</Th>
              <Th>Type</Th>
              <Th>Seats</Th>
              <Th>Devices kept here</Th>
              <Th>Carts</Th>
              <Th>Assigned to room</Th>
              {canWrite && <Th className="text-right">Actions</Th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {building.rooms.map((room) => (
              <tr key={room.id} className="hover:bg-slate-50/60">
                <Td>
                  <span className="flex flex-wrap items-center gap-2">
                    <Link to={`/locations/rooms/${room.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {room.name}
                    </Link>
                    <PlaceDates place={room} />
                  </span>
                </Td>
                <Td className="text-slate-500">{room.roomNumber ?? '—'}</Td>
                <Td className="text-slate-500">{ROOM_KIND_LABEL[room.kind]}</Td>
                <Td className="text-slate-500">{room.capacity ?? '—'}</Td>
                <Td>{room._count?.assets ?? 0}</Td>
                <Td>{room._count?.carts ? room._count.carts : <span className="text-slate-400">0</span>}</Td>
                <Td>{room._count?.checkouts ? <Badge tone="blue">{room._count.checkouts}</Badge> : <span className="text-slate-400">0</span>}</Td>
                {canWrite && (
                  <Td className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => onEditRoom(room)}>
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => onDelete({ kind: 'room', id: room.id, name: room.name })}>
                        Delete
                      </Button>
                    )}
                    {edvora && (
                      <Button size="sm" variant="ghost" onClick={() => onRemove({ kind: 'room', id: room.id, name: room.name })}>
                        Remove (Edvora)
                      </Button>
                    )}
                  </Td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

export function LocationsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const buildings = useBuildings();
  const [buildingModal, setBuildingModal] = useState<{ building?: Building } | null>(null);
  const [roomModal, setRoomModal] = useState<{ room?: Room; buildingId?: string } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: 'building' | 'room'; id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<{ kind: 'building' | 'room'; id: string; name: string } | null>(null);
  const edvora = useIsEdvoraStaff();
  const [reporting, setReporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(deleting!.kind === 'building' ? `/buildings/${deleting!.id}` : `/rooms/${deleting!.id}`),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`Deleted ${deleting!.name}`);
      setDeleting(null);
    },
    onError: (err) => {
      setDeleting(null);
      toast.error(errorMessage(err));
    },
  });

  const canWrite = can('TECHNICIAN');
  const isAdmin = can('ADMIN');
  const campus = buildings.data?.filter((building) => building.kind !== 'WAREHOUSE') ?? [];
  const warehouses = buildings.data?.filter((building) => building.kind === 'WAREHOUSE') ?? [];
  const card = (building: Building) => (
    <BuildingCard
      key={building.id}
      building={building}
      canWrite={canWrite}
      isAdmin={isAdmin}
      edvora={edvora}
      onRemove={setRemoving}
      onAddRoom={() => setRoomModal({ buildingId: building.id })}
      onEdit={() => setBuildingModal({ building })}
      onEditRoom={(room) => setRoomModal({ room })}
      onDelete={setDeleting}
    />
  );

  return (
    <>
      <PageHeader
        title="Locations"
        description="Buildings, rooms, and off-campus warehouses: where devices are kept, and who is responsible for each room over time."
        actions={
          <>
            <Button icon={<FileDown />} onClick={() => setReporting(true)}>
              Who was responsible
            </Button>
            {isAdmin && (
              <Button icon={<Upload />} onClick={() => setImporting(true)}>
                Import from Blackbaud
              </Button>
            )}
            {canWrite && (
              <>
                <Button icon={<CalendarRange />} onClick={() => navigate('/locations/rollover')}>
                  Room rollover
                </Button>
                <Button icon={<DoorOpen />} onClick={() => setRoomModal({})} disabled={!buildings.data?.length}>
                  Add room
                </Button>
                <Button variant="primary" icon={<Plus />} onClick={() => setBuildingModal({})}>
                  Add building
                </Button>
              </>
            )}
          </>
        }
      />

      {buildings.isLoading ? (
        <PageLoader />
      ) : buildings.error ? (
        <ErrorState error={buildings.error} onRetry={() => buildings.refetch()} />
      ) : buildings.data!.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Building2 />}
            title="No buildings yet"
            description="Add your campus buildings, then the rooms inside them."
            action={canWrite && <Button variant="primary" icon={<Plus />} onClick={() => setBuildingModal({})}>Add building</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-8">
          <div className="space-y-6">{campus.map(card)}</div>
          {warehouses.length > 0 && (
            <section className="space-y-4">
              <div>
                <h2 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Warehouses · off campus</h2>
                <p className="text-sm text-slate-500">Depot stock, end-of-life inventory, and district loaners kept away from the school.</p>
              </div>
              {warehouses.map(card)}
            </section>
          )}
        </div>
      )}

      <BuildingModal open={!!buildingModal} onClose={() => setBuildingModal(null)} building={buildingModal?.building} />
      <RoomModal open={!!roomModal} onClose={() => setRoomModal(null)} room={roomModal?.room} buildingId={roomModal?.buildingId} />
      <ImportBuildingsModal open={importing} onClose={() => setImporting(false)} />
      <ResponsibilityReportModal open={reporting} onClose={() => setReporting(false)} />
      <RemoveRecordsDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        subjects={removing?.kind === 'building' ? { buildingIds: [removing.id] } : { roomIds: removing ? [removing.id] : [] }}
      />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete ${deleting?.name}?`}
        description={
          deleting?.kind === 'building'
            ? 'Only a building with no history can be deleted: no carts, no devices ever kept there, and no one ever responsible for its rooms. Otherwise, close it instead.'
            : 'Only a room with no history can be deleted: no carts, no devices ever kept there, and no one ever responsible for it. Otherwise, close it instead.'
        }
        confirmLabel="Delete"
      />
    </>
  );
}
